import {
  type FC,
  type ReactNode,
  createContext,
  useContext,
  useState,
} from 'react';
import type {
  GoogleMapsAPIState,
  GoogleMapsContextValue,
} from '@/types/googleMaps';
import {
  isGoogleMapsLoaded,
  hasGoogleMapsScript,
  loadGoogleMapsScript,
  waitForGoogleMapsLoad,
} from '@/utils/googleMapsLoader';

const GoogleMapsContext = createContext<GoogleMapsContextValue | null>(null);

interface GoogleMapsProviderProps {
  children: ReactNode;
}

export const GoogleMapsProvider: FC<GoogleMapsProviderProps> = ({
  children,
}) => {
  const [state, setState] = useState<GoogleMapsAPIState>(() => ({
    isLoaded: isGoogleMapsLoaded(),
    isLoading: false,
    error: null,
  }));

  const loadAPI = async (apiKey: string): Promise<void> => {
    try {
      // If already loaded, resolve immediately
      if (state.isLoaded || isGoogleMapsLoaded()) {
        setState(prev => ({ ...prev, isLoaded: true, isLoading: false }));
        return;
      }

      // If already loading, wait for current loading to complete
      if (state.isLoading) {
        return new Promise((resolve, reject) => {
          const checkLoaded = () => {
            if (state.isLoaded) {
              resolve();
            } else if (state.error) {
              reject(state.error);
            } else {
              setTimeout(checkLoaded, 100);
            }
          };
          checkLoaded();
        });
      }

      setState(prev => ({ ...prev, isLoading: true, error: null }));

      // Check if script already exists but not loaded yet
      if (hasGoogleMapsScript()) {
        await waitForGoogleMapsLoad();
      } else {
        await loadGoogleMapsScript(apiKey);
      }

      setState({ isLoaded: true, isLoading: false, error: null });
    } catch (error) {
      const err =
        error instanceof Error ? error : new Error('Unknown error occurred');
      setState({ isLoaded: false, isLoading: false, error: err });
      throw err;
    }
  };

  const contextValue: GoogleMapsContextValue = {
    state,
    loadAPI,
  };

  return (
    <GoogleMapsContext.Provider value={contextValue}>
      {children}
    </GoogleMapsContext.Provider>
  );
};

export const useGoogleMapsContext = (): GoogleMapsContextValue => {
  const context = useContext(GoogleMapsContext);
  if (!context) {
    throw new Error(
      'useGoogleMapsContext must be used within a GoogleMapsProvider'
    );
  }
  return context;
};
